import React, { useState, useEffect, useMemo } from "react";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Typography,
  Alert,
  Row,
  Col,
  Tag,
  Switch,
  Modal,
  message,
} from "antd";
import {
  EyeOutlined,
  ArrowRightOutlined,
  SettingOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useTender } from "../../contexts/TenderContext";
import { useDevMode } from "@/contexts/DevModeContext";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useNavigate } from "react-router-dom";
import { COLORS, formatUnit } from "../../utils/constants";
import { formatCurrency } from "../../utils/calculations";
import MainLayout from "../layout/MainLayout";
import {
  ManualPreliminaryItem,
  MainService,
  TableValidationError,
  PrelimsItemRecord,
} from "../../types/tender.types";
import { useQueryClient } from "@tanstack/react-query";
import {
  updateCurrentBoqVersionFromState,
  forceSaveBothBoqEndpoints,
} from "../../utils/boqVersionUtils";
import {
  generateAutoCalculatedItems,
  calculateManualItemAmounts,
  calculateMainServicesTotal,
  calculatePumpStationTotal,
  validatePreliminaryItems,
  DEFAULT_MANUAL_ITEMS,
  MAIN_SERVICE_RATES,
} from "../../utils/preliminaryCalculations";
import { preEliminairesRates } from "../../mock";
import {
  exportPreliminaryItemsSimpleToExcel,
  generatePreliminaryItemsSimpleTemplate,
  PreliminaryItemsSimpleExcelData,
} from "../../utils/preliminaryItemsSimpleExcelUtils";
import { ExcelImportModal } from "../common/ExcelImportExport";
import { useTableRenderDeferral } from "../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../common/TableLoadingPlaceholder";
import NumberInput from "../common/NumberInput";
import {
  PRELIMS_MAPPING,
  LEGACY_METHOD_RELATED_GROUP_IDS,
  findPrelimsItemByDescription,
} from "../../utils/prelimsMapping";
import type {
  PrelimsCategory,
  PrelimsGroup,
  PrelimsItem,
} from "../../utils/prelimsMapping";
import {
  ColumnConfigModal,
  ConfigField,
  selectStyles,
} from "../common/ColumnConfigModal";
import { useItemLibrary } from "../../hooks/useItemLibrary";
import type { ItemLibraryItem } from "../../api/itemLibrary";

// Builds a PrelimsItemRecord for a mapping item, merging in the saved
// quantity/rate (keyed by the same deterministic id used everywhere this
// mapping is consumed — PrelimsBillSummary, RsSumAnalysisBillSummary,
// JobTypeSelection's import, and here). `grp` is omitted for items that sit
// directly on the category (no group layer) so their id keeps the same
// `prelim-${cat.id}-...` shape without an empty segment.
const buildPrelimRecord = (
  cat: PrelimsCategory,
  grp: PrelimsGroup | null,
  item: PrelimsItem,
  savedItemsMap: Map<string, PrelimsItemRecord>,
  resourceCodeMap: Map<string, ItemLibraryItem>,
): PrelimsItemRecord => {
  const idSuffix = item.description.replace(/[^a-zA-Z0-9]/g, "-");
  const deterministicId = grp
    ? `prelim-${cat.id}-${grp.id}-${idSuffix}`
    : `prelim-${cat.id}-${idSuffix}`;
  const legacyGroupId = grp ? undefined : LEGACY_METHOD_RELATED_GROUP_IDS[item.description];
  const legacyId = legacyGroupId ? `prelim-${cat.id}-${legacyGroupId}-${idSuffix}` : undefined;
  const saved = savedItemsMap.get(deterministicId) ?? (legacyId ? savedItemsMap.get(legacyId) : undefined);
  // Variant rows (e.g. "CBR testing option(s)") let the user pick which
  // code applies via a dropdown; fall back to the mapping's default code
  // when nothing saved yet, or when the saved code isn't one of this row's
  // valid variants.
  const selectedVariant = item.variants?.find((v) => v.code === saved?.code);
  const code = item.variants ? selectedVariant?.code ?? item.code : item.code;
  const description = selectedVariant?.label ?? item.description;
  const libItem = resourceCodeMap.get((code || "").trim().toUpperCase());
  const quantity = saved?.quantity ?? 0;
  // Use saved rate only when it is non-zero (a real user/library value).
  // rate=0 means "not yet resolved" — e.g. items imported via bulk import
  // before the item library loaded — so fall through to the library rate.
  const libRate = libItem ? libItem.nettRate || libItem.baseRate || 0 : 0;
  const rate = saved && saved.rate > 0 ? saved.rate : libRate;
  return {
    id: deterministicId,
    description,
    quantity,
    unit: item.unit,
    rate,
    amount: quantity * rate,
    code: code || undefined,
    category: cat.label,
    group: grp?.label,
    variants: item.variants,
    defaultCode: item.variants ? item.code : undefined,
    baseDescription: item.variants ? item.description : undefined,
  };
};

const PreliminaryItems: React.FC = () => {
  const showDevMode = useDevMode();
  useScrollToTop({ behavior: "smooth", delay: 100 });
  const { state, updateField, saveTender } = useTender();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { saveStatus } = useAutoSave();
  // Defers this step's table's first mount by one paint cycle so a tender
  // with a lot of preliminary items doesn't make this step look frozen while
  // the table's initial render/layout blocks the main thread - see
  // useTableRenderDeferral's own doc comment. Purely a perceived-lag fix:
  // once it flips false the table renders exactly as before.
  const isTableLoading = useTableRenderDeferral();
  const [_validationErrors, setValidationErrors] = useState<
    TableValidationError[]
  >([]);

  const handleSaveAndContinue = async () => {
    updateField("status", "In-progress");
    updateField("boqStatus", "In-progress");
    updateField("lastModified", new Date());
    saveTender();
    try {
      // `state` is still the closure captured at render time — the updateField calls
      // above dispatch asynchronously and don't flow back into it until the next
      // render, so build the payload from an explicit merged copy instead. `boqStatus`
      // always takes precedence over `status` in transformToPayload (see storage.ts),
      // so it must be forced to "In-progress" too or a stale value (e.g. left over from
      // a prior Submit Tender) would override "In-progress" right back to itself.
      await updateCurrentBoqVersionFromState(
        { ...state, status: "In-progress", boqStatus: "In-progress", lastModified: new Date() },
        "Preliminary items updated",
      );
      queryClient.invalidateQueries({
        queryKey: ["boq-versions", state.projectName],
      });
    } catch (error) {
      console.error("Error saving preliminary items to BOQ API:", error);
    }
    navigate("/roads-sewers/foul-sewers");
  };

  const handleSaveAndExit = async () => {
    setIsSaving(true);
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });
      updateField("status", "In-progress");
      updateField("lastModified", new Date());
      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));
      await forceSaveBothBoqEndpoints(
        state,
        "Save and exit from Preliminary Items",
        {},
        undefined,
        undefined,
      );
      queryClient.invalidateQueries({
        queryKey: ["boq-versions", state.projectName],
      });
      message.destroy("saveTender");
      setTimeout(() => navigate("/"), 500);
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      message.error({
        content: `Failed to save tender: ${err.response?.data?.message || err.message || "Unknown error"}`,
        key: "saveTender",
        duration: 5,
      });
    } finally {
      setIsSaving(false);
    }
  };
  const [editingCell, setEditingCell] = useState<{
    table: string;
    id: string;
    field: string;
  } | null>(null);

  const [isViewRatesModalOpen, setIsViewRatesModalOpen] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [cbrTestingCodeDraft, setCbrTestingCodeDraft] = useState("");

  const preliminaryData = state.preliminaryItems || {
    autoCalculated: [],
    manualItems: [],
    mainServices: [],
    pumpStation: null,
    totals: {
      autoCalculatedTotal: 0,
      manualItemsTotal: 0,
      mainServicesTotal: 0,
      pumpStationTotal: 0,
      grandTotal: 0,
    },
  };

  // Item library for rate lookups by resource code
  const { data: itemLibraryData } = useItemLibrary(state.region || "");
  const resourceCodeMap = useMemo(() => {
    const map = new Map<string, ItemLibraryItem>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, item);
    });
    return map;
  }, [itemLibraryData]);

  // Derive the full display list synchronously from PRELIMS_MAPPING, merging
  // saved quantities/rates from state. Runs on every render so the correct
  // grouped structure is always available from the very first paint.
  // Key saved items by deterministic ID (category+group+description) so that
  // duplicate descriptions across different groups (e.g. "Dismantle heras fencing
  // upon completion" appears in 3 groups) are stored and retrieved independently.
  const savedItemsMap = useMemo(() => {
    const map = new Map<string, PrelimsItemRecord>();
    (state.preliminaryItems?.preliminaryItems || []).forEach((saved) => {
      map.set(saved.id, saved);
    });
    return map;
  }, [state.preliminaryItems?.preliminaryItems]);

  const preliminaryItemsData: PrelimsItemRecord[] = useMemo(
    () =>
      PRELIMS_MAPPING.flatMap((cat) => [
        ...(cat.items ?? []).map((item) =>
          buildPrelimRecord(cat, null, item, savedItemsMap, resourceCodeMap),
        ),
        ...cat.groups.flatMap((grp) =>
          grp.items.map((item) =>
            buildPrelimRecord(cat, grp, item, savedItemsMap, resourceCodeMap),
          ),
        ),
      ]),
    [savedItemsMap, resourceCodeMap],
  );

  // Update preliminary item record
  const updatePreliminaryItem = (
    id: string,
    field: keyof PrelimsItemRecord,
    value: PrelimsItemRecord[keyof PrelimsItemRecord],
  ) => {
    const updatedData = preliminaryItemsData.map((record) => {
      if (record.id === id) {
        const updated = { ...record, [field]: value };

        // If quantity changes, recalculate amount
        if (field === "quantity") {
          updated.amount = (value as number) * updated.rate;
        }

        // Switching a variant row's code (e.g. CBR testing option) means the
        // old rate no longer applies — re-resolve from the item library for
        // the new code, then recalculate amount. The description follows the
        // selected variant's label too (falls back to the row's own label
        // when cleared back to "Select").
        if (field === "code") {
          const libItem = resourceCodeMap.get(
            ((value as string) || "").trim().toUpperCase(),
          );
          updated.rate = libItem ? libItem.nettRate || libItem.baseRate || 0 : 0;
          updated.amount = updated.quantity * updated.rate;
          const matchedVariant = record.variants?.find((v) => v.code === value);
          updated.description =
            matchedVariant?.label ?? record.baseDescription ?? record.description;
        }

        return updated;
      }
      return record;
    });

    updateField("preliminaryItems", {
      ...state.preliminaryItems,
      // Keep a row even at quantity=0 when its code has been switched away
      // from the mapping default (e.g. a CBR testing option picked in
      // Configure before a quantity is entered) — otherwise the next render
      // would silently revert the selection back to the default code.
      preliminaryItems: updatedData.filter((r) => {
        if (r.quantity > 0) return true;
        return !!r.code && r.code !== r.defaultCode;
      }),
    });
  };

  // "CBR tests" row — its code (and so rate/description of testing) is
  // user-selectable via the Configure modal rather than fixed by the mapping.
  // Identified by `variants` (only this row has any) rather than `description`,
  // since the description itself now switches with the selected variant.
  const cbrRecord = preliminaryItemsData.find(
    (record) => record.variants && record.variants.length > 0,
  );
  const cbrTestingOptions = findPrelimsItemByDescription("CBR tests")?.item
    .variants;

  const openConfigModal = () => {
    setCbrTestingCodeDraft(cbrRecord?.code || cbrTestingOptions?.[0]?.code || "");
    setConfigModalVisible(true);
  };

  const applyConfigModal = () => {
    if (cbrRecord) {
      updatePreliminaryItem(cbrRecord.id, "code", cbrTestingCodeDraft);
    }
    setConfigModalVisible(false);
  };

  // Calculate preliminary items total
  const calculatePreliminaryTotal = () => {
    return preliminaryItemsData.reduce((sum, record) => sum + record.amount, 0);
  };

  // Calculate rates for View Rates modal
  const calculateAllPreliminaryRates = () => {
    const ratesData: any[] = [];

    preEliminairesRates.forEach((category) => {
      category.items.forEach((item) => {
        // Skip items with null rates
        if (item.rate === null) return;

        // Find matching preliminary item record
        const matchingRecord = preliminaryItemsData.find(
          (record) => record.description === item.description,
        );

        const quantity = matchingRecord ? matchingRecord.quantity : 0;
        const amount = matchingRecord ? matchingRecord.amount : 0;

        ratesData.push({
          category: category.category,
          description: item.description,
          quantity: quantity,
          unit: item.unit,
          rate: item.rate || 0,
          amount: amount,
        });
      });
    });

    return ratesData;
  };

  // Auto-calculate items when source data changes
  useEffect(() => {
    const newAutoCalculated = generateAutoCalculatedItems(
      state.foulSewers,
      state.stormSewers,
      state.roads || {
        roadTypes: [],
        cutFillSections: [],
        carriageway: {
          kerbs: { precastConcrete: 0, naturalStone: 0, conservation: 0 },
          footpaths: { concrete: 0, tarmac: 0, block: 0 },
          crossovers: { standard: 0, heavy: 0 },
          roadMarkings: { whiteLine: 0, yellowLine: 0, hatching: 0 },
        },
        cappingLayer: { enabled: false, thickness: 150 },
      },
    );

    // Initialize manual items if empty
    let manualItems = preliminaryData.manualItems;
    if (manualItems.length === 0) {
      manualItems = DEFAULT_MANUAL_ITEMS.map((item, index) => ({
        ...item,
        id: `manual-${index}`,
        amount: item.isPercentage ? 0 : item.quantity * item.rate,
        unit: item.unit as any,
      }));
    }

    // Calculate totals
    const autoCalculatedTotal = newAutoCalculated
      .filter((item) => item.included)
      .reduce((sum, item) => sum + item.amount, 0);

    const manualItemsTotal = manualItems.reduce(
      (sum, item) => sum + item.amount,
      0,
    );
    const mainServicesTotal = calculateMainServicesTotal(
      preliminaryData.mainServices,
    );
    const pumpStationTotal = calculatePumpStationTotal(
      preliminaryData.pumpStation,
    );
    const grandTotal =
      autoCalculatedTotal +
      manualItemsTotal +
      mainServicesTotal +
      pumpStationTotal;

    // Update manual items with percentage calculations
    const updatedManualItems = calculateManualItemAmounts(
      manualItems,
      grandTotal,
    );

    const hasChanges =
      JSON.stringify(newAutoCalculated) !==
        JSON.stringify(preliminaryData.autoCalculated) ||
      JSON.stringify(updatedManualItems) !==
        JSON.stringify(preliminaryData.manualItems);

    if (hasChanges) {
      updateField("preliminaryItems", {
        ...preliminaryData,
        autoCalculated: newAutoCalculated,
        manualItems: updatedManualItems,
        totals: {
          autoCalculatedTotal,
          manualItemsTotal: updatedManualItems.reduce(
            (sum, item) => sum + item.amount,
            0,
          ),
          mainServicesTotal,
          pumpStationTotal,
          grandTotal:
            autoCalculatedTotal +
            updatedManualItems.reduce((sum, item) => sum + item.amount, 0) +
            mainServicesTotal +
            pumpStationTotal,
        },
      });
    }
  }, [
    state.foulSewers,
    state.stormSewers,
    state.roads,
    preliminaryData.manualItems,
    preliminaryData.mainServices,
    preliminaryData.pumpStation,
  ]);

  // Unified Excel Export
  const handleUnifiedExport = async () => {
    try {
      const data: PreliminaryItemsSimpleExcelData = {
        preliminaryItems: preliminaryItemsData.map((record) => ({
          id: record.id,
          description: record.description,
          quantity: record.quantity,
          unit: record.unit,
          rate: record.rate,
          amount: record.amount,
        })),
      };

      await exportPreliminaryItemsSimpleToExcel(
        data,
        state.projectName || "PreliminaryItems",
      );
    } catch (error) {
      console.error("Export failed:", error);
      alert(`Export failed: ${error.message}`);
    }
  };

  // Generate Template
  const handleDownloadTemplate = async () => {
    try {
      await generatePreliminaryItemsSimpleTemplate(
        state.projectName || "PreliminaryItems",
      );
    } catch (error) {
      console.error("Template generation failed:", error);
      alert(`Template generation failed: ${error.message}`);
    }
  };

  // Import Takeoff — col 3 (index 2) = description, col 8 (index 7) = quantity
  const handleUnifiedImport = async (file: File) => {
    setImportLoading(true);
    try {
      const XLSX = await import("xlsx");
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });

      const sheetName = workbook.SheetNames.find(
        (n) => n.trim().toLowerCase() === "prelims",
      );

      if (!sheetName) {
        alert(
          `No "Prelims" sheet found. Available sheets: ${workbook.SheetNames.join(", ")}`,
        );
        return;
      }

      const worksheet = workbook.Sheets[sheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        raw: true,
        blankrows: true,
        defval: null,
      }) as unknown[][];

      // Some descriptions (e.g. "Erect heras fencing on feet", "Dismantle heras
      // fencing upon completion") are repeated verbatim under multiple parent
      // groups in the Prelims sheet (Tree Protection Fencing, Boundary Protection
      // Fencing, etc.), each with its own quantity. Matching by description text
      // alone collapses all of them into a single shared value. Instead, queue up
      // each description's item IDs in the same top-to-bottom order they appear in
      // both PRELIMS_MAPPING and the sheet, and consume one per row encountered —
      // so repeated items are assigned to their correct parent group in order.
      const idQueueByDescription = new Map<string, string[]>();
      preliminaryItemsData.forEach((item) => {
        const key = item.description.trim().toLowerCase();
        const queue = idQueueByDescription.get(key);
        if (queue) queue.push(item.id);
        else idQueueByDescription.set(key, [item.id]);
      });

      // Build item ID → quantity map from the sheet
      const importedQtyById = new Map<string, number>();
      for (const row of rawRows) {
        const desc = (row as unknown[])[2];
        const qty = (row as unknown[])[7];
        if (
          typeof desc === "string" &&
          desc.trim() &&
          qty != null &&
          !isNaN(Number(qty)) &&
          Number(qty) > 0
        ) {
          const id = idQueueByDescription
            .get(desc.trim().toLowerCase())
            ?.shift();
          if (id) importedQtyById.set(id, Number(qty));
        }
      }

      if (importedQtyById.size === 0) {
        alert(
          "No valid rows found in the Prelims sheet (check column positions).",
        );
        return;
      }

      // Merge imported quantities into the full items list
      let matchCount = 0;
      const updatedItems: PrelimsItemRecord[] = preliminaryItemsData
        .map((item) => {
          const importedQty = importedQtyById.get(item.id);
          if (importedQty !== undefined) {
            matchCount++;
            return {
              ...item,
              quantity: importedQty,
              amount: importedQty * item.rate,
            };
          }
          return item;
        })
        .filter((item) => item.quantity > 0);

      updateField("preliminaryItems", {
        ...state.preliminaryItems,
        preliminaryItems: updatedItems,
      });

      setImportModalVisible(false);
      alert(`Import complete — ${matchCount} item(s) matched and updated.`);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error("Import failed:", error);
      alert(`Import failed: ${msg}`);
    } finally {
      setImportLoading(false);
    }
  };

  const toggleAutoCalculatedItem = (id: string) => {
    const updatedItems = preliminaryData.autoCalculated.map((item) =>
      item.id === id ? { ...item, included: !item.included } : item,
    );

    updateField("preliminaryItems", {
      ...preliminaryData,
      autoCalculated: updatedItems,
    });
  };

  const addManualItem = () => {
    const newItem: ManualPreliminaryItem = {
      id: `manual-${Date.now()}`,
      description: "",
      quantity: 1,
      unit: "nr",
      rate: 0,
      amount: 0,
      isPercentage: false,
    };

    updateField("preliminaryItems", {
      ...preliminaryData,
      manualItems: [...preliminaryData.manualItems, newItem],
    });
  };

  const updateManualItem = (
    id: string,
    field: keyof ManualPreliminaryItem,
    value: any,
  ) => {
    const updatedItems = preliminaryData.manualItems.map((item) =>
      item.id === id ? { ...item, [field]: value } : item,
    );

    updateField("preliminaryItems", {
      ...preliminaryData,
      manualItems: updatedItems,
    });
  };

  const deleteManualItem = (id: string) => {
    const updatedItems = preliminaryData.manualItems.filter(
      (item) => item.id !== id,
    );

    updateField("preliminaryItems", {
      ...preliminaryData,
      manualItems: updatedItems,
    });
  };

  const addMainService = () => {
    const newService: MainService = {
      id: `service-${Date.now()}`,
      type: "Water",
      diameter: "25mm",
      length: 0,
      depthCategory: 1,
      rate: MAIN_SERVICE_RATES.Water["25mm"],
      amount: 0,
    };

    updateField("preliminaryItems", {
      ...preliminaryData,
      mainServices: [...preliminaryData.mainServices, newService],
    });
  };

  const updateMainService = (
    id: string,
    field: keyof MainService,
    value: any,
  ) => {
    const updatedServices = preliminaryData.mainServices.map((service) => {
      if (service.id === id) {
        const updated = { ...service, [field]: value };

        // Update rate when type or diameter changes
        if (field === "type" || field === "diameter") {
          const serviceType = field === "type" ? value : service.type;
          const diameter = field === "diameter" ? value : service.diameter;
          const serviceRates =
            MAIN_SERVICE_RATES[serviceType as keyof typeof MAIN_SERVICE_RATES];
          updated.rate = serviceRates
            ? (serviceRates as any)[diameter] || 0
            : 0;
        }

        // Calculate amount
        updated.amount = updated.length * updated.rate;

        return updated;
      }
      return service;
    });

    updateField("preliminaryItems", {
      ...preliminaryData,
      mainServices: updatedServices,
    });
  };

  const deleteMainService = (id: string) => {
    const updatedServices = preliminaryData.mainServices.filter(
      (service) => service.id !== id,
    );

    updateField("preliminaryItems", {
      ...preliminaryData,
      mainServices: updatedServices,
    });
  };

  const handleCellEdit = (
    table: string,
    id: string,
    field: string,
    value: any,
  ) => {
    if (table === "manualItems") {
      updateManualItem(id, field as keyof ManualPreliminaryItem, value);
    } else if (table === "mainServices") {
      updateMainService(id, field as keyof MainService, value);
    }
    setEditingCell(null);
  };

  const isEditing = (table: string, id: string, field: string) => {
    return (
      editingCell?.table === table &&
      editingCell?.id === id &&
      editingCell?.field === field
    );
  };

  const importStandardPreliminaries = () => {
    const standardItems = DEFAULT_MANUAL_ITEMS.map((item, index) => ({
      ...item,
      id: `standard-${Date.now()}-${index}`,
      amount: item.isPercentage ? 0 : item.quantity * item.rate,
      unit: item.unit as any,
    }));

    updateField("preliminaryItems", {
      ...preliminaryData,
      manualItems: [...preliminaryData.manualItems, ...standardItems],
    });
  };

  // Validate data
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    const errors = validatePreliminaryItems(preliminaryData.manualItems);
    const validationErrorsList: TableValidationError[] = errors.map(
      (error, index) => ({
        id: `validation-${index}`,
        field: "general",
        message: error,
      }),
    );

    setValidationErrors(validationErrorsList);
    setIsValid(validationErrorsList.length === 0);
  }, [preliminaryData.manualItems]);

  // Grouped table: category → group → items, always fully expanded
  const [stickyOffset, setStickyOffset] = useState(230);

  // Some groups carry an explanatory note (matches the descriptive text rows
  // under some group headers in the source R&S Excel template) — look it up
  // by category+group label so it can be shown under the group row.
  const groupDescriptions = useMemo(() => {
    const map = new Map<string, string>();
    PRELIMS_MAPPING.forEach((cat) => {
      cat.groups.forEach((grp) => {
        if (grp.description)
          map.set(`${cat.label}::${grp.label}`, grp.description);
      });
    });
    return map;
  }, []);

  // Categories authored with root-level items (PrelimsCategory.description)
  // carry their own explanatory note directly, with no group layer at all.
  const categoryDescriptions = useMemo(() => {
    const map = new Map<string, string>();
    PRELIMS_MAPPING.forEach((cat) => {
      if (cat.description) map.set(cat.label, cat.description);
    });
    return map;
  }, []);

  const groupedTableData = useMemo(() => {
    const catMap = new Map<
      string,
      { rootItems: PrelimsItemRecord[]; groups: Map<string, PrelimsItemRecord[]> }
    >();
    const unassigned: PrelimsItemRecord[] = [];

    preliminaryItemsData.forEach((record) => {
      if (!record.category) {
        unassigned.push(record);
        return;
      }
      if (!catMap.has(record.category))
        catMap.set(record.category, { rootItems: [], groups: new Map() });
      const cat = catMap.get(record.category)!;
      if (record.group) {
        if (!cat.groups.has(record.group)) cat.groups.set(record.group, []);
        cat.groups.get(record.group)!.push(record);
      } else {
        cat.rootItems.push(record);
      }
    });

    const rows: Record<string, unknown>[] = [];
    const allKeys: React.Key[] = [];

    catMap.forEach(({ rootItems, groups: grpMap }, catName) => {
      const catKey = `cat-${catName}`;
      allKeys.push(catKey);

      const rootItemRows = rootItems.map((i) => ({
        key: `item-${i.id}`,
        rowType: "item",
        ...i,
      }));

      // A category with exactly one named group and no separate root items
      // carries no real grouping value, so fold it into the category row
      // instead of showing a redundant nested group header. Only surface a
      // note when the group has an explicit `description` (e.g. Boundary
      // Protection Fencing's "Supply and erect heras fencing on rubber
      // feet.") — groups without one (Security, Earthworks Validation)
      // show just the category label, with no note manufactured from the
      // group's label.
      if (grpMap.size === 1 && rootItemRows.length === 0) {
        const [[grpName, items]] = grpMap;
        rows.push({
          key: catKey,
          rowType: "category",
          label: catName,
          description: groupDescriptions.get(`${catName}::${grpName}`),
          amount: items.reduce((s, i) => s + i.amount, 0),
          children: items.map((i) => ({
            key: `item-${i.id}`,
            rowType: "item",
            ...i,
          })),
        });
        return;
      }

      const groupRows: Record<string, unknown>[] = [];
      grpMap.forEach((items, grpName) => {
        const grpKey = `grp-${catName}-${grpName}`;
        allKeys.push(grpKey);
        const grpTotal = items.reduce((s, i) => s + i.amount, 0);
        groupRows.push({
          key: grpKey,
          rowType: "group",
          label: grpName,
          description: groupDescriptions.get(`${catName}::${grpName}`),
          amount: grpTotal,
          children: items.map((i) => ({
            key: `item-${i.id}`,
            rowType: "item",
            ...i,
          })),
        });
      });

      const children = [...rootItemRows, ...groupRows];
      const catTotal = children.reduce(
        (s, c) => s + ((c as { amount: number }).amount || 0),
        0,
      );
      rows.push({
        key: catKey,
        rowType: "category",
        label: catName,
        description: categoryDescriptions.get(catName),
        amount: catTotal,
        children,
      });
    });

    if (unassigned.length > 0) {
      const unCatKey = "cat-unassigned";
      allKeys.push(unCatKey);
      rows.push({
        key: unCatKey,
        rowType: "category",
        label: "Unassigned",
        amount: unassigned.reduce((s, i) => s + i.amount, 0),
        children: unassigned.map((i) => ({
          key: `item-${i.id}`,
          rowType: "item",
          ...i,
        })),
      });
    }

    return { rows, allKeys };
  }, [preliminaryItemsData, groupDescriptions, categoryDescriptions]);

  useEffect(() => {
    const el = document.querySelector(
      ".prelim-sticky-header",
    ) as HTMLElement | null;
    if (el) setStickyOffset(144 + el.offsetHeight);
  }, []);

  

  return (
    <MainLayout canProceed={isValid} showFooter={false}>
      <Card
        className="prelim-card shadow-sm"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: "8px", overflow: "visible" }}
      >
        {/* Sticky header */}
        <div
          className="prelim-sticky-header"
          style={{
            position: "sticky",
            top: "144px",
            backgroundColor: "#fff",
            zIndex: 98,
            borderRadius: "8px 8px 0 0",
            isolation: "isolate",
          }}
        >
          {/* Header with border */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <div className="flex justify-between items-center">
              <div>
                <Typography.Title
                  level={3}
                  style={{
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#111827",
                    marginBottom: "8px",
                  }}
                >
                  Preliminary Items
                </Typography.Title>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: "14px", color: "#6B7280" }}
                >
                  Configure preliminary site establishment and operational items
                </Typography.Text>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Button
                  size="small"
                  icon={<SettingOutlined />}
                  onClick={openConfigModal}
                  type="default"
                  className="configure-headers-btn"
                  style={{
                    marginTop: "16px",
                    height: "32px",
                    borderRadius: "6px",
                    fontWeight: 500,
                    borderColor: "#1890ff",
                    color: "#1890ff",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Configure
                </Button>
                {showDevMode && (
                  <Button
                    type="text"
                    icon={<DownloadOutlined />}
                    onClick={() => setImportModalVisible(true)}
                    style={{ color: "#1890ff", fontWeight: 500 }}
                  >
                    Import Take-off
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* end prelim-sticky-header */}

        {/* Content section */}
        <div className="px-6 py-6">
          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            bordered
            size="middle"
            scroll={{ x: 900 }}
            pagination={false}
            dataSource={groupedTableData.rows}
            rowKey="key"
            sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
            expandable={{
              childrenColumnName: "children",
              expandedRowKeys: groupedTableData.allKeys,
              expandIcon: () => null,
            }}
            rowClassName={(record) => {
              if (record.rowType === "category")
                return "bg-indigo-50 font-semibold";
              if (record.rowType === "group")
                return "bg-indigo-50 font-semibold";
              return "";
            }}
            columns={[
              {
                title: "Description",
                dataIndex: "description",
                render: (_, record) => {
                  if (
                    record.rowType === "category" ||
                    record.rowType === "group"
                  ) {
                    const note = record.description as string | undefined;
                    // Stay inline-block (not a plain block div) so this cell's content
                    // continues to sit beside antd's expand/collapse icon instead of
                    // wrapping onto a new line below it.
                    return (
                      <span
                        style={{
                          display: "inline-block",
                          verticalAlign: "middle",
                        }}
                      >
                        <Typography.Text strong className="text-indigo-600">
                          {record.label as string}
                        </Typography.Text>
                        {note && (
                          <div>
                            <Typography.Text
                              type="secondary"
                              italic
                              style={{ fontSize: 12, whiteSpace: "pre-line" }}
                            >
                              {note}
                            </Typography.Text>
                          </div>
                        )}
                      </span>
                    );
                  }
                  return (
                    <Typography.Text>
                      {record.description as string}
                    </Typography.Text>
                  );
                },
              },
              {
                title: "Quantity",
                dataIndex: "quantity",
                width: 120,
                render: (_, record) => {
                  if (record.rowType !== "item") return null;
                  return (
                    <NumberInput
                      value={record.quantity as number}
                      onChange={(value) =>
                        updatePreliminaryItem(
                          record.id as string,
                          "quantity",
                          value || 0,
                        )
                      }
                      decimalPlaces={3}
                      positiveOnly
                      placeholder="0.000"
                      style={{ width: "100%", textAlign: "right" }}
                    />
                  );
                },
              },
              {
                title: "Unit",
                dataIndex: "unit",
                width: 80,
                align: "center" as const,
                render: (_, record) => {
                  if (record.rowType !== "item") return null;
                  const unit = (record.unit as string) || "";
                  return (
                    <span className="text-gray-600">{formatUnit(unit)}</span>
                  );
                },
              },
              ...(showDevMode
                ? [
                    {
                      title: "Code",
                      dataIndex: "code",
                      width: 110,
                      render: (_: unknown, record: Record<string, unknown>) => {
                        if (record.rowType !== "item") return null;
                        const code = (record.code as string) || "";
                        if (!code) return <Typography.Text type="secondary">—</Typography.Text>;
                        const libItem = resourceCodeMap.get(code.trim().toUpperCase());
                        const libRate = libItem ? libItem.nettRate || libItem.baseRate || 0 : undefined;
                        return (
                          <span>
                            <Tag color="blue">{code}</Tag>
                            {libRate !== undefined && (
                              <div style={{ color: "#52c41a", fontSize: 12, marginTop: 2 }}>
                                {formatCurrency(libRate)}
                              </div>
                            )}
                          </span>
                        );
                      },
                    },
                  ]
                : []),
            ]}
          />
          )}

          {/* Validation Errors */}
          {_validationErrors.length > 0 && (
            <Alert
              message={
                <div>
                  <div className="font-semibold mb-2">
                    Please fix the following errors:
                  </div>
                  <ul className="m-0 pl-5">
                    {_validationErrors.map((error, index) => (
                      <li key={index}>{error.message}</li>
                    ))}
                  </ul>
                </div>
              }
              type="error"
              style={{ marginTop: "16px" }}
            />
          )}

          {/* Success Message */}
          {isValid && saveStatus === "saved" && (
            <Alert
              message="Preliminary items data saved successfully. You can proceed to the next step."
              type="success"
              style={{ marginTop: "16px" }}
            />
          )}
        </div>

        {/* Footer with action buttons */}
        <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "16px" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              onClick={handleSaveAndExit}
              loading={isSaving}
              style={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
              }}
            >
              Save and Exit
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>

      {/* Configure Modal */}
      <ColumnConfigModal
        title="Configure"
        open={configModalVisible}
        onClose={() => setConfigModalVisible(false)}
        onApply={applyConfigModal}
        width={500}
        isViewMode={state.isViewMode}
      >
        <ConfigField label="CBR testing option">
          <select
            value={cbrTestingCodeDraft}
            onChange={(e) => setCbrTestingCodeDraft(e.target.value)}
            style={selectStyles}
          >
            <option value="">Select</option>
            {cbrTestingOptions?.map((opt) => (
              <option key={opt.code} value={opt.code}>
                {opt.label}
              </option>
            ))}
          </select>
        </ConfigField>
      </ColumnConfigModal>

      {/* View Rates Modal */}
      <Modal
        title="Preliminary Items - View Rates"
        open={isViewRatesModalOpen}
        onCancel={() => setIsViewRatesModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewRatesModalOpen(false)}>
            Close
          </Button>,
        ]}
        width={1000}
      >
        <div>
          {preEliminairesRates.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-6">
              <Typography.Title level={4} className="mb-4 text-blue-900">
                {category.category}
              </Typography.Title>

              {(() => {
                const tableData = category.items
                  .filter((item) => item.rate !== null) // Filter out items with null rates
                  .map((item, index) => {
                    // Find matching preliminary item record for filled values
                    const matchingRecord = preliminaryItemsData.find(
                      (record) => record.description === item.description,
                    );

                    const quantity = matchingRecord
                      ? matchingRecord.quantity
                      : 0;
                    const amount = matchingRecord ? matchingRecord.amount : 0;

                    return {
                      key: index + 1,
                      description: item.description,
                      quantity: quantity,
                      unit: item.unit,
                      rate: item.rate || 0,
                      amount: amount,
                    };
                  });

                const sectionTotal = tableData.reduce(
                  (sum, item) => sum + item.amount,
                  0,
                );

                return (
                  <>
                    <Table
                      dataSource={tableData}
                      columns={[
                        {
                          title: "Description",
                          dataIndex: "description",
                          key: "description",
                          width: "40%",
                        },
                        {
                          title: "Quantity",
                          dataIndex: "quantity",
                          key: "quantity",
                          width: "15%",
                          render: (value: number) => value.toFixed(2),
                        },
                        {
                          title: "Unit",
                          dataIndex: "unit",
                          key: "unit",
                          width: "10%",
                          align: "center" as const,
                          render: (value: string) => formatUnit(value),
                        },
                        {
                          title: "Rate (£)",
                          dataIndex: "rate",
                          key: "rate",
                          width: "15%",
                          render: (value: number) =>
                            `£${(value || 0).toFixed(2)}`,
                        },
                        {
                          title: "Amount (£)",
                          dataIndex: "amount",
                          key: "amount",
                          width: "20%",
                          render: (value: number) =>
                            `£${(value || 0).toFixed(2)}`,
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                    <div className="flex justify-end mt-2 mb-4">
                      <div className="bg-blue-50 p-2 rounded">
                        <Typography.Text strong className="text-blue-900">
                          Section Total: £{sectionTotal.toFixed(2)}
                        </Typography.Text>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          ))}

          {/* Grand Total */}
          {(() => {
            const allRatesData = calculateAllPreliminaryRates();
            const grandTotal = allRatesData.reduce(
              (sum, item) => sum + item.amount,
              0,
            );

            return (
              <div className="mt-8 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
                <div className="flex justify-between items-center">
                  <Typography.Title level={3} className="text-blue-900 mb-0">
                    Grand Total
                  </Typography.Title>
                  <Typography.Title level={2} className="text-blue-900 mb-0">
                    £{grandTotal.toFixed(2)}
                  </Typography.Title>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  <p>
                    Total includes all preliminary items with filled quantities
                    and calculated amounts.
                  </p>
                </div>
              </div>
            );
          })()}
        </div>
      </Modal>

      {/* Excel Import Modal */}
      <ExcelImportModal
        onImport={handleUnifiedImport}
        onDownloadTemplate={handleDownloadTemplate}
        importLoading={importLoading}
        importModalVisible={importModalVisible}
        setImportModalVisible={setImportModalVisible}
      />

      <style>{`
        .prelim-card { overflow: visible !important; }
        .prelim-card .ant-card-body { overflow: visible !important; }
        .prelim-sticky-header { box-shadow: none; will-change: transform; transform: translateZ(0); z-index: 98 !important; }
        .ant-table-thead > tr > th {
          background-color: ${COLORS.tableHeaderBg} !important;
        }
      `}</style>
    </MainLayout>
  );
};

export default PreliminaryItems;
